import {
  Porting,
  PortingPac,
  PortingStac,
  QualifiedDiscount,
  Sim,
  SimSettings,
  SimSettingsInput,
} from '@generated/gql/graphql';
import { BooleanSelection } from '@src/types/index';

import { SimSettingsFormValues } from './types';

/**
 * Joins the flat discount list returned by `mobileTariffs` onto the SIMs it
 * applies to. Discounts are server-derived and change on every requote, so they
 * are never stored alongside the user's selection — they are projected on read.
 *
 * `targetQuoteId` matches `sim.id`, as a SIM, its tariff and its quote all
 * inherit the same quote id.
 */
export const withQualifiedDiscounts = (
  sims: Sim[],
  discounts: QualifiedDiscount[]
): Sim[] =>
  sims.map((sim) => ({
    ...sim,
    selectedTariff: {
      ...sim.selectedTariff,
      discounts: discounts.filter(
        ({ targetQuoteId }) => targetQuoteId === sim.id
      ),
    },
  }));

export const isPortingStac = (porting: Porting): porting is PortingStac => {
  return porting.__typename === 'PortingSTAC';
};

export const isPortingPac = (porting: Porting): porting is PortingPac => {
  return porting.__typename === 'PortingPAC';
};

export const parseSimSettingsFormValues = (
  values: SimSettingsFormValues
): Omit<SimSettings, '__typename'> => {
  const {
    isKeepNumber,
    keepNumber,
    pacNumber,
    isCancelNumber,
    cancelNumber,
    stacNumber,
    budgetControl,
  } = values;

  const budgetControlValue = budgetControl === BooleanSelection.Yes;
  const keepNumberValue = isKeepNumber === BooleanSelection.Yes;
  const cancelPreviousContractValue = isCancelNumber === BooleanSelection.Yes;

  let porting: Porting | null = null;

  if (isKeepNumber === BooleanSelection.Yes) {
    porting = {
      __typename: 'PortingPAC',
      mobileNumber: keepNumber,
      pac: pacNumber,
    };
  } else if (isCancelNumber === BooleanSelection.Yes) {
    porting = {
      __typename: 'PortingSTAC',
      mobileNumber: cancelNumber,
      stac: stacNumber,
    };
  }

  return {
    budgetControl: budgetControlValue,
    keepNumber: keepNumberValue,
    cancelPreviousContract: cancelPreviousContractValue,
    porting,
  };
};

export const toSimSettingsInput = (settings: SimSettings): SimSettingsInput => {
  const { budgetControl, keepNumber, cancelPreviousContract, porting } =
    settings;

  return {
    budgetControl,
    keepNumber,
    cancelPreviousContract,
    portingPAC:
      porting && isPortingPac(porting)
        ? { mobileNumber: porting.mobileNumber, pac: porting.pac }
        : null,
    portingSTAC:
      porting && isPortingStac(porting)
        ? { mobileNumber: porting.mobileNumber, stac: porting.stac }
        : null,
  };
};
